// Captures the last visible frame of a video URL as a JPEG Blob, entirely in
// the browser. Used by the "Extend clip" flow: the captured frame is uploaded
// and becomes the seed image for the next video generation in the chain.
//
// CORS: cdn.photovortex.com serves with access-control-allow-origin, so the
// crossOrigin="anonymous" canvas read works (verified in Editor/CropForVideo.jsx
// which uses the same pattern on images).
//
// Why -0.04s epsilon: some browsers seek-to-end snaps to a keyframe-aligned
// position that's one frame past the displayed end. 40ms safely lands on the
// last visible frame across 24/25/30fps content. Frame-exact extraction would
// require ffmpeg server-side.
export async function captureLastFrame(videoUrl) {
  if (!videoUrl) return null;

  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = videoUrl;

  try {
    await new Promise((resolve, reject) => {
      const onError = () => reject(new Error("Video failed to load"));
      video.addEventListener("loadedmetadata", resolve, { once: true });
      video.addEventListener("error", onError, { once: true });
    });

    const targetTime = Math.max(0, video.duration - 0.04);
    await new Promise((resolve, reject) => {
      const onError = () => reject(new Error("Video seek failed"));
      video.addEventListener("seeked", resolve, { once: true });
      video.addEventListener("error", onError, { once: true });
      video.currentTime = targetTime;
    });

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
    });
  } catch {
    return null;
  }
}
